import { useState } from "react";
import {
  Link,
  useLoaderData,
  useNavigate,
  useRouteLoaderData,
  json,
} from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { motion } from "framer-motion";
import { getUserId, getPhoneNumber } from "../middleware/getToken";
import {
  FaStar,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
  FaUser,
  FaPhoneAlt,
} from "react-icons/fa";
import LoadingComponent from "../shared/component/LoadingComponent";
import {
  trackInitiateCheckout,
  trackCall,
} from "../shared/Pixel/metaPixelEvents";

export default function HotelRooms() {
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [userName, setUserName] = useState("");
  const [loading, setIsLoading] = useState(false);

  const data = useLoaderData();
  const hotel = data?.hotel || {};
  const navigate = useNavigate();
  const token = useRouteLoaderData("root");
  const userId = getUserId();
  const phoneNumber = getPhoneNumber();

  const handleSubmit = async (e) => {
    e.preventDefault();

    trackInitiateCheckout({
      roomId: hotel.id,
      roomName: hotel.name,
      totalPrice: hotel.price,
    });

    const hotelId = hotel.id;

    const bookingData = {
      userId,
      hotelId,
      checkInDate,
      checkOutDate,
      numberOfGuests,
      totalPrice: hotel.price,
      userName,
      phoneNumber,
    };

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/bookings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...bookingData }),
        }
      );

      const resData = await response.json();

      if (!response.ok) {
        throw new Error(resData.message);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error creating booking:", error);
    }

    navigate(`/booked/${userId}`);
    setIsLoading(false);
  };

  const handleCall = (phoneNumber) => {
    trackCall({
      id: hotel.id,
      name: hotel.name,
      price: hotel.price,
    });
    window.location.href = `tel:${phoneNumber}`;
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <Link
            to="../../"
            className="inline-flex items-center text-gray-500 hover:text-gray-800 font-medium mb-12 transition duration-300"
          >
            <motion.span whileHover={{ x: -3 }} transition={{ duration: 0.2 }}>
              ←
            </motion.span>
            <span className="ml-2">Back to Hotels</span>
          </Link>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-24"
          >
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="lg:flex">
                <div className="lg:w-1/2">
                  <Carousel
                    showThumbs={false}
                    showStatus={false}
                    autoPlay={false}
                    infiniteLoop={true}
                    interval={5000}
                    className="h-96 lg:h-full"
                  >
                    {hotel.images?.map((img, index) => (
                      <motion.div
                        key={index}
                        className="h-96 lg:h-full"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.4 }}
                      >
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/${img}`}
                          alt={`${hotel.name} - Image ${index + 1}`}
                          className="object-cover w-full h-full"
                        />
                      </motion.div>
                    ))}
                  </Carousel>
                </div>

                <div className="lg:w-1/2 p-12">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mb-12"
                  >
                    <h1 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">
                      {hotel.name}
                    </h1>
                    <div className="flex items-center mb-6">
                      <FaStar className="w-5 h-5 text-amber-400 mr-1" />
                      <span className="text-gray-600 mr-6">4.8</span>
                      <FaMapMarkerAlt className="w-5 h-5 text-gray-400 mr-1" />
                      <span className="text-gray-600">{hotel.address}</span>
                    </div>

                    <p className="text-4xl font-semibold text-gray-900 mb-6">
                      {hotel.price}
                      <span className="text-base text-gray-500 ml-2">
                        per night
                      </span>
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      {hotel.description}
                    </p>
                  </motion.div>

                  <motion.form
                    onSubmit={handleSubmit}
                    className="space-y-8"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          <FaCalendarAlt className="inline mr-2" />
                          Check-in Date
                        </label>
                        <input
                          type="date"
                          value={checkInDate}
                          onChange={(e) => setCheckInDate(e.target.value)}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          <FaCalendarAlt className="inline mr-2" />
                          Check-out Date
                        </label>
                        <input
                          type="date"
                          value={checkOutDate}
                          onChange={(e) => setCheckOutDate(e.target.value)}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        <FaUsers className="inline mr-2" />
                        Number of Guests
                      </label>
                      <input
                        type="number"
                        value={numberOfGuests}
                        onChange={(e) => setNumberOfGuests(e.target.value)}
                        required
                        min="1"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        <FaUser className="inline mr-2" />
                        Your Name
                      </label>
                      <input
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <motion.button
                        onClick={() => handleCall(hotel.phone)}
                        className="w-full bg-amber-500 text-white font-medium py-4 px-6 rounded-xl flex items-center justify-center transition duration-300 hover:bg-amber-600"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <FaPhoneAlt className="mr-2" />
                        Book via Call
                      </motion.button>
                      <motion.button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 text-white font-medium py-4 px-6 rounded-xl transition duration-300 hover:bg-blue-700"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {loading ? (
                          <LoadingComponent />
                        ) : (
                          "Book now & Pay at hotel"
                        )}
                      </motion.button>
                    </div>
                  </motion.form>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
            <div className="flex overflow-x-auto space-x-6 scroll-smooth scrollbar-hide snap-x snap-mandatory pb-6">
              {hotel.images?.map((img, index) => (
                <motion.div
                  key={index}
                  className="relative flex-none w-72 h-72 first:ml-4 last:mr-4 snap-start"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/${img}`}
                    alt={`${hotel.name} - Image ${index + 1}`}
                    className="w-full h-full object-cover rounded-2xl shadow-lg transition-transform duration-300 hover:shadow-2xl"
                  />
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-black/5 group-hover:ring-black/10" />
                </motion.div>
              ))}
            </div>
            <div className="absolute inset-y-0 left-0 w-48 pointer-events-none bg-gradient-to-r from-gray-50 to-transparent" />
            <div className="absolute inset-y-0 right-0 w-48 pointer-events-none bg-gradient-to-l from-gray-50 to-transparent" />
          </div>
        </div>
        <p></p>

        <div className="bg-white py-24"></div>
      </div>
    </>
  );
}

export async function loader({ request, params }) {
  const id = params.id;
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/hoteles/${id}`
    );
    const resData = await response.json();

    if (!response.ok) {
      throw new Error("Internal server err, Please try again later.");
    }

    return resData;
  } catch (err) {
    throw json(
      { message: "Field to find hotel, Please try again later." },
      { status: 500 }
    );
  }
}
